import React, { useContext, useState } from "react";
import { Modal } from "react-bootstrap";
import useTranslator from "../../hooks/useTranslator";
import {
  formatPropertyAddress,
  haveValue,
  toast,
  validatePhoneNumber,
} from "../../helper/common";
import LandLordContext from "../../context/landlord/LandLordContext";
import AuthContext from "../../context/auth/AuthContext";
import { useParams } from "react-router-dom";
import copy from "copy-to-clipboard";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { FaWhatsapp } from "react-icons/fa6";
import { Button, Icon, Tabs, TextArea, TextField } from "../design";
import "./InviteTenantModal.css";

const InviteTenantModal = ({
  show,
  onHide,
  propertyId,
  property,
  onSuccess,
}) => {
  const { _t } = useTranslator();
  const { sendInvitationToTenant } = useContext(LandLordContext);
  const { user } = useContext(AuthContext);
  const { workspaceId } = useParams();

  const [activeTab, setActiveTab] = useState(null);
  const [sharerEmail, setSharerEmail] = useState("");
  const [sharerName, setSharerName] = useState("");
  const [sharerPhone, setSharerPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sendInvitationLoading, setSendInvitationLoading] = useState(false);

  // Build invite link — same as workspace invite link
  let inviteLink = `${window.location.protocol}//${process.env.REACT_APP_URL_NAME}/home?requestProfileLink=${user?._id}_${workspaceId}`;
  if (haveValue(propertyId)) {
    inviteLink += `&property_id=${propertyId}`;
  }

  const handleClose = () => {
    setSharerEmail("");
    setSharerName("");
    setSharerPhone("");
    setMessage("");
    setActiveTab(null);
    onHide();
  };

  const handleCopyLink = () => {
    copy(inviteLink);
    toast.success(_t("linkCopied"));
  };

  // Property address
  const propertyObj = property || {};
  const addressLine1 = formatPropertyAddress(propertyObj);
  const city = propertyObj?.city || "";
  const postalCode = propertyObj?.postal_code || "";
  const addressLine2 = [city, postalCode].filter(Boolean).join(", ");

  // --- Email send ---
  const sendInvitationViaEmail = async (e) => {
    e.preventDefault();
    if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(sharerEmail)) {
      setSendInvitationLoading(true);
      try {
        const response = await sendInvitationToTenant({
          type: "email",
          name: sharerName,
          email: sharerEmail,
          message,
          property_id: propertyId,
        });
        const data = response.data;
        if (data.message) {
          toast.success(data.message);
        }
        handleClose();
        onSuccess?.();
      } catch (error) {
        const response = error.response;
        if (response?.data?.err) {
          toast.error(response.data.err);
        }
      } finally {
        setSendInvitationLoading(false);
      }
    } else {
      toast.error(_t("invalidEmailAddress"));
    }
  };

  // --- WhatsApp send ---
  const sendInvitationViaPhone = async (e) => {
    e.preventDefault();
    const validate = validatePhoneNumber(sharerPhone?.replace(/\+/g, ""));
    if (!validate.isValid) {
      toast.error(validate.message);
      return;
    }
    setSendInvitationLoading(true);
    try {
      const response = await sendInvitationToTenant({
        type: "phone",
        name: sharerName,
        phone: sharerPhone,
        message,
        property_id: propertyId,
      });
      const data = response.data;
      if (data.message) {
        toast.success(data.message);
      }
      handleClose();
      onSuccess?.();
    } catch (error) {
      const response = error.response;
      if (response?.data?.err) {
        toast.error(response.data.err);
      }
    } finally {
      setSendInvitationLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className="newmodalui wrapper-modal inviteTenantModal max-w-lg"
    >
      <Modal.Header>
        <Modal.Title>{_t("inviteTenant")}</Modal.Title>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          className="topRightIcon"
          aria-label={_t("close")}
          iconStart={<Icon name="close" size={16} />}
          onClick={handleClose}
        />
      </Modal.Header>

      <Modal.Body>
        {/* ── Property info card ── */}
        {(addressLine1 || addressLine2) && (
          <div className="itm-property-card">
            <div className="itm-property-icon-wrap">
              <Icon name="building" size={20} />
            </div>
            <div className="itm-property-text">
              {addressLine1 && (
                <span className="itm-property-title">{addressLine1}</span>
              )}
              {addressLine2 && (
                <span className="itm-property-sub">{addressLine2}</span>
              )}
            </div>
          </div>
        )}

        {/* ── Invite link row ── */}
        <div className="itm-link-row">
          <span className="itm-link-icon">
            <Icon name="link" size={15} />
          </span>
          <span className="itm-link-text" title={inviteLink}>
            {inviteLink}
          </span>
          <Button
            variant="accent"
            size="md"
            iconStart={<Icon name="copy" size={16} />}
            onClick={handleCopyLink}
          >
            {_t("copyLink") || "Copy link"}
          </Button>
        </div>

        {/* ── or divider ── */}
        <div className="itm-or-divider">
          <span>{_t("or") || "or"}</span>
        </div>

        {/* ── Tab toggle ── */}
        <Tabs
          className="itm-tabs"
          size="lg"
          block
          value={activeTab ?? ""}
          onChange={setActiveTab}
          items={[
            {
              value: "email",
              label: _t("sendViaEmail") || "Send via email",
              icon: <Icon name="mail" />,
            },
            {
              value: "whatsapp",
              label: _t("sendViaWhatsApp") || "Send via WhatsApp",
              icon: <FaWhatsapp size={20} />,
            },
          ]}
        />

        {/* ── Email form ── */}
        {activeTab === "email" && (
          <form onSubmit={sendInvitationViaEmail} className="itm-form">
            <div className="itm-input-row">
              <TextField
                id="itm-tenant-name"
                label={_t("tenantName")}
                type="text"
                placeholder={_t("tenantName") || "Tenant name"}
                value={sharerName}
                required
                onChange={(e) => setSharerName(e.target.value)}
                onBeforeInput={(e) => {
                  const isValidChar = /^[A-Za-zÀ-ÿ\s'-]+$/.test(e.data);
                  if (!isValidChar) e.preventDefault();
                }}
              />
              <TextField
                id="itm-tenant-email"
                label={_t("tenantEmail")}
                type="email"
                placeholder={_t("tenantEmail") || "tenant@email.com"}
                value={sharerEmail}
                required
                onChange={(e) => setSharerEmail(e.target.value)}
                onBeforeInput={(e) => {
                  if (!e.data) return;
                  const isValid = /^[a-z0-9._%+-@]+$/.test(e.data);
                  if (!isValid) {
                    e.preventDefault();
                    return;
                  }
                  const existingValue = e.target.value;
                  const totalAtCount =
                    (existingValue + e.data).split("@").length - 1;
                  if (totalAtCount > 1) e.preventDefault();
                }}
              />
            </div>
            <TextArea
              id="itm-message-email"
              label={_t("messageTenantOptional")}
              placeholder={
                _t("messageTenantOptional") || "Message to tenant (optional)"
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
            />
            <p className="itm-hint">
              {_t("messageIncludedInEmail") ||
                "This message will be included in the invitation email."}
            </p>
            <div className="d-flex justify-content-center">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={sendInvitationLoading}
              >
                {_t("sendInvitation") || "Send invitation"}
              </Button>
            </div>
          </form>
        )}

        {/* ── WhatsApp form ── */}
        {activeTab === "whatsapp" && (
          <form onSubmit={sendInvitationViaPhone} className="itm-form">
            <div className="itm-input-row">
              <TextField
                id="itm-tenant-name"
                label={_t("tenantName")}
                type="text"
                placeholder={_t("tenantName") || "Tenant name"}
                value={sharerName}
                required
                onChange={(e) => setSharerName(e.target.value)}
                onBeforeInput={(e) => {
                  const isValidChar = /^[A-Za-zÀ-ÿ\s'-]+$/.test(e.data);
                  if (!isValidChar) e.preventDefault();
                }}
              />
              <div className="forminput customInput mb-0">
                <label>{_t("phoneNumber")}</label>
                <PhoneInput
                  className="phone_input_info"
                  defaultCountry="es"
                  value={sharerPhone}
                  onChange={(phone) => setSharerPhone(phone)}
                  placeholder="600 000 000"
                />
              </div>
            </div>
            <TextArea
              id="itm-message-whatsapp"
              label={_t("messageTenantOptional")}
              placeholder={
                _t("messageTenantOptional") || "Message to tenant (optional)"
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
            />
            <p className="itm-hint">
              {_t("messageIncludedInWhatsApp") ||
                "This message will be included in the WhatsApp message."}
            </p>
            <div className="d-flex justify-content-center">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={sendInvitationLoading}
              >
                {_t("sendInvitation") || "Send invitation"}
              </Button>
            </div>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default InviteTenantModal;
